// While another account is active in this browser, the tab holds: nothing it sends could reach the right account.
// This notice covers every route with an opaque page of its own (never `.modal-backdrop`, which `motion.ts` keeps on
// screen while it leaves, with the unsaved text in it), shows what the person typed and has not saved so they can
// copy it, and continues as the other account on request. The page behind stays mounted and goes inert, so nothing
// typed there is lost when the hold clears; the two live regions beside it stay out of `inert`, so what is said
// while held is still heard.

import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { continueAsAnother } from '../account-switch.js';
import { accountHold, type AccountHold } from '../app-state.js';
import { textRows } from '../editors/text-rows.js';
import { t } from '../i18n.js';
import { activeAccount } from '../offline-edits/session-row.js';
import { requestsHeld } from '../request.js';
import { say } from '../status.js';
import { joinedUnsavedText, uncopyableHeld } from '../unsaved-text.js';
import { holdsVersion } from '../update-holds.js';
import { CopyButton } from './copy-button.js';

import type { JSX } from 'preact';

const LIVE_REGIONS = new Set(['announce-polite', 'announce-assertive']);

const titleOf = (hold: AccountHold): string => {
  if (hold.actor === undefined) {
    return t('app.anotherAccount.titleSignedOut');
  }

  return hold.name === undefined ? t('app.anotherAccount.titleNoName') : t('app.anotherAccount.title', { name: hold.name });
};

const continueOf = (hold: AccountHold): string => {
  if (hold.actor === undefined) {
    return t('app.anotherAccount.continueSignedOut');
  }

  return hold.name === undefined ? t('app.anotherAccount.continueNoName') : t('app.anotherAccount.continue', { name: hold.name });
};

/** Makes every other child of the notice's parent inert but the live regions; answers the undo. */
function inertBehind(notice: HTMLElement): () => void {
  const behind = [...(notice.parentElement?.children ?? [])].filter(
    (child) => child !== notice && !LIVE_REGIONS.has(child.id) && !child.hasAttribute('inert'),
  );
  for (const element of behind) {
    element.setAttribute('inert', '');
  }

  return (): void => {
    for (const element of behind) {
      element.removeAttribute('inert');
    }
  };
}

/**
 * Takes every open modal dialog and popover out of the top layer, where they would sit above the notice and leave it
 * inert, and answers the undo: each dialog still in the page opens again as it was. A popover is a hint, shown again
 * on the next hover or focus.
 */
function leaveTopLayer(doc: Document): () => void {
  for (const popover of doc.querySelectorAll<HTMLElement>('[popover]')) {
    if (isOpenPopover(popover)) {
      popover.hidePopover();
    }
  }

  const closed = [...doc.querySelectorAll('dialog')].filter((dialog) => dialog.open).map((dialog) => {
    const modal = matchesModal(dialog);
    // The close is the notice's, not the person's: whoever listens for it must not tear the dialog down.
    const swallow = (event: Event): void => {
      event.stopImmediatePropagation();
    };

    dialog.addEventListener('close', swallow, { capture: true });
    dialog.close();
    return { dialog, modal, swallow };
  });

  return (): void => {
    for (const { dialog, modal, swallow } of closed) {
      dialog.removeEventListener('close', swallow, { capture: true });
      if (dialog.isConnected && !dialog.open) {
        if (modal) {
          dialog.showModal();
        } else {
          dialog.show();
        }
      }
    }
  };
}

const matchesModal = (dialog: HTMLDialogElement): boolean => safeMatches(dialog, ':modal');
const isOpenPopover = (element: HTMLElement): boolean => safeMatches(element, ':popover-open');

/** `matches` for a selector an older engine may not parse, which answers false there. */
function safeMatches(element: Element, selector: string): boolean {
  try {
    return element.matches(selector);
  } catch {
    return false;
  }
}

/** Whether the account the tab holds for has changes waiting on this device; false until known. Asked again on
 *  each hold change and each take or release of an update hold (the outbox holds while rows wait). */
function useKeptWaiting(hold: AccountHold | undefined, version: number): boolean {
  const [kept, setKept] = useState(false);
  useEffect(() => {
    setKept(false);
    const accountId = activeAccount();
    if (hold === undefined || accountId === undefined) {
      return undefined;
    }

    let current = true;
    // Loaded on demand, as the switch loads it: the output window must not carry the outbox.
    void import('../offline-edits/outbox.js')
      .then(async ({ waitingRows }) => waitingRows(accountId))
      .then(
        (rows) => {
          if (current) {
            setKept(rows.length > 0);
          }
        },
        () => undefined,
      );
    return (): void => {
      current = false;
    };
  }, [hold, version]);

  return kept;
}

/** Covers the tab while `accountHold` is set; renders nothing otherwise. */
export function AnotherAccountNotice({ proceed = continueAsAnother }: { readonly proceed?: () => void }): JSX.Element | null {
  const hold = accountHold.value;
  const held = hold !== undefined;
  const notice = useRef<HTMLDivElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const [losing, setLosing] = useState(false);
  const kept = useKeptWaiting(hold, holdsVersion());
  const unsaved = useMemo(() => (hold === undefined ? '' : joinedUnsavedText()), [hold]);
  const uncopyable = hold !== undefined && uncopyableHeld();

  useEffect(() => {
    const node = notice.current;
    if (!held || node === null) {
      return undefined;
    }

    const reopen = leaveTopLayer(node.ownerDocument);
    const undo = inertBehind(node);
    title.current?.focus();
    return (): void => {
      undo();
      reopen();
      setLosing(false);
      if (!requestsHeld()) {
        say('polite', t('app.anotherAccount.resumed'));
      }
    };
  }, [held]);

  if (hold === undefined) {
    return null;
  }

  const signedOut = hold.actor === undefined;
  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key !== 'Tab') {
      return;
    }

    event.preventDefault();
    const buttons = [...(notice.current?.querySelectorAll('button') ?? [])];
    const at = buttons.findIndex((button) => button === document.activeElement);
    const last = buttons.length - 1;
    const next = event.shiftKey ? (at <= 0 ? last : at - 1) : (at === -1 || at === last ? 0 : at + 1);
    buttons[next]?.focus();
  };

  return (
    <div ref={notice} class="account-notice">
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="account-notice-title"
        aria-describedby="account-notice-body"
        onKeyDown={onKeyDown}
      >
        <h2 ref={title} id="account-notice-title" class="account-notice-text" tabIndex={-1}>{titleOf(hold)}</h2>
        <p id="account-notice-body" class="account-notice-text">
          {t(signedOut ? 'app.anotherAccount.bodySignedOut' : 'app.anotherAccount.body')}
        </p>
        <label for="account-notice-unsaved">{t('app.anotherAccount.unsavedLabel')}</label>
        <textarea id="account-notice-unsaved" rows={textRows(unsaved, 6)} readOnly value={unsaved} />
        <CopyButton text={unsaved} />
        {kept ? <p class="account-notice-text">{t(signedOut ? 'app.anotherAccount.keptSignedOut' : 'app.anotherAccount.kept')}</p> : null}
        {uncopyable ? <p class="account-notice-text">{t('app.anotherAccount.cannotCopy')}</p> : null}
        <button
          type="button"
          class="button-primary"
          onClick={(event) => {
            if (losing && event.detail > 1) {
              // The second click of a double-click that asked the question: only a fresh press answers it.
              return;
            }

            if (!losing && uncopyable) {
              setLosing(true);
              return;
            }

            proceed();
          }}
        >
          {losing && uncopyable ? t('app.anotherAccount.continueLosing') : continueOf(hold)}
        </button>
      </div>
    </div>
  );
}
